import axios from 'axios'

const BASE_URL = `${process.env.VUE_APP_baseUrl}/api/manifests`

export default {
  namespaced: true,

  state: {
    manifests: [],
    manifest: null,
    availableOrders: [],
    outsourcingCompanies: [],
    pagination: {
      count: 0,
      next: null,
      previous: null,
    },
    isLoading: false,
    error: null,
  },

  mutations: {
    SET_MANIFESTS(state, {
      results, count, next, previous,
    }) {
      state.manifests = results
      state.pagination = { count, next, previous }
    },
    SET_MANIFEST(state, manifest) {
      state.manifest = manifest
    },
    UPDATE_MANIFEST_ORDER_STATUSES(state, statuses) {
      if (!state.manifest || !Array.isArray(state.manifest.manifest_orders)) return

      const statusByOrderId = statuses.reduce((acc, item) => {
        acc[item.order_id] = item
        return acc
      }, {})

      const manifestOrders = state.manifest.manifest_orders.map(orderRow => {
        const updatedStatus = statusByOrderId[orderRow.order_id]
        if (!updatedStatus) return orderRow

        return {
          ...orderRow,
          order_status: updatedStatus.order_status,
          order_status_display: updatedStatus.order_status_display,
          order_status_changed_at: updatedStatus.order_status_changed_at,
          treated_at: updatedStatus.treated_at,
          treated_status: updatedStatus.treated_status,
          treated_status_display: updatedStatus.treated_status_display,
          treatment_source: updatedStatus.treatment_source,
        }
      })
      const treatedCount = manifestOrders.filter(orderRow => orderRow.treated_at).length
      const ordersCount = state.manifest.orders_count || manifestOrders.length
      let manifestStatus = state.manifest.status
      if (manifestStatus !== 'annule') {
        manifestStatus = 'ouvert'
        if (ordersCount && treatedCount >= ordersCount) {
          manifestStatus = 'termine'
        } else if (treatedCount > 0) {
          manifestStatus = 'en_cours'
        }
      }

      state.manifest = {
        ...state.manifest,
        status: manifestStatus,
        manifest_orders: manifestOrders,
        treated_count: treatedCount,
        remaining_count: Math.max(ordersCount - treatedCount, 0),
      }
    },
    SET_AVAILABLE_ORDERS(state, {
      results, count, next, previous,
    }) {
      state.availableOrders = results
      state.pagination = { count, next, previous }
    },
    SET_OUTSOURCING_COMPANIES(state, companies) {
      state.outsourcingCompanies = companies
    },
    SET_LOADING(state, val) {
      state.isLoading = val
    },
    SET_ERROR(state, err) {
      state.error = err
    },
  },

  actions: {
    async fetchManifests({ commit }, params = {}) {
      commit('SET_LOADING', true)
      try {
        const { data } = await axios.get(`${BASE_URL}/`, { params })
        commit('SET_MANIFESTS', {
          results: data.results || data,
          count: data.count || 0,
          next: data.next || null,
          previous: data.previous || null,
        })
      } catch (err) {
        commit('SET_ERROR', err)
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchManifest({ commit }, id) {
      commit('SET_LOADING', true)
      try {
        const { data } = await axios.get(`${BASE_URL}/${id}/`)
        commit('SET_MANIFEST', data)
        return data
      } catch (err) {
        commit('SET_ERROR', err)
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchManifestOrderStatuses({ commit }, manifestId) {
      const { data } = await axios.get(`${BASE_URL}/${manifestId}/order-statuses/`)
      commit('UPDATE_MANIFEST_ORDER_STATUSES', data)
      return data
    },

    async createManifest(_, payload) {
      const { data } = await axios.post(`${BASE_URL}/create/`, payload)
      return data
    },

    async addOrders({ commit }, { manifestId, orderIds }) {
      const { data } = await axios.post(`${BASE_URL}/${manifestId}/add-orders/`, {
        order_ids: orderIds,
      })
      commit('SET_MANIFEST', data.manifest)
      return data
    },

    async removeOrders(_, { manifestId, orderIds }) {
      const { data } = await axios.post(`${BASE_URL}/${manifestId}/remove-orders/`, {
        order_ids: orderIds,
      })
      return data
    },

    async cancelManifest({ commit }, { manifestId, reason }) {
      const { data } = await axios.post(`${BASE_URL}/${manifestId}/cancel/`, {
        reason,
      })
      commit('SET_MANIFEST', data)
      return data
    },

    async fetchAvailableOrders({ commit }, params = {}) {
      commit('SET_LOADING', true)
      try {
        const { data } = await axios.get(`${BASE_URL}/available-orders/`, { params })
        commit('SET_AVAILABLE_ORDERS', {
          results: data.results || data,
          count: data.count || 0,
          next: data.next || null,
          previous: data.previous || null,
        })
      } catch (err) {
        commit('SET_ERROR', err)
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchOutsourcingCompanies({ commit }) {
      const { data } = await axios.get(`${BASE_URL}/outsourcing-companies/`)
      commit('SET_OUTSOURCING_COMPANIES', data)
      return data
    },
  },

  getters: {
    manifests: s => s.manifests,
    manifest: s => s.manifest,
    availableOrders: s => s.availableOrders,
    outsourcingCompanies: s => s.outsourcingCompanies,
    isLoading: s => s.isLoading,
    pagination: s => s.pagination,
  },
}
